import { CatalogResourceDc, CatalogResourceType } from "@evergis/api";
import { ComplexOptionText, IconTypesKeys, IOption, Menu } from "@evergis/ui";
import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../../../../../evergis/api";
import { useAddByLinkDialog, useSelectFilesDialog } from "../../../../../../hooks/useDialogs";
import { useToggle } from "../../../../../../hooks/useToggle";
import { JSONAttributeValue } from "../../types";
import { StyledMenuPopup } from "./styled";

export interface AttachmentAddButtonProps {
  hiddenItems?: JSONAttributeValue[];
  onApply: (newItems: JSONAttributeValue[]) => void;
  offset?: [number, number];
}

export const AttachmentAddButton: FC<AttachmentAddButtonProps> = ({ hiddenItems, onApply, offset, children }) => {
  const [isVisiblePopup, togglePopup] = useToggle(false);
  const { t } = useTranslation("featureCard");

  const openSelectFiles = useSelectFilesDialog();
  const openAddByLink = useAddByLinkDialog();

  const handlers = useMemo(
    () => ({
      fromCatalog: () =>
        openSelectFiles([""], {
          onApply: (selectedResources: CatalogResourceDc[]) => {
            onApply(
              selectedResources.map(({ resourceId, contentType, name, updatedAt }) => ({
                isExternal: false,
                link: resourceId,
                name,
                mimeType: contentType,
                date: updatedAt,
              })),
            );
          },
          hiddenItems: hiddenItems?.filter(({ isExternal }) => !isExternal).map(({ link }) => link),
          filters: {
            resourceTypes: [CatalogResourceType.File],
          },
        }),
      byLink: () =>
        openAddByLink([""], {
          onApply: async (link: string) => {
            const fileInfo = await api.file.getFileInfo({ url: link });

            onApply([
              {
                isExternal: true,
                link,
                name: fileInfo?.fileName,
                mimeType: fileInfo?.contentType,
                date: new Date().toString(),
              },
            ]);
          },
          hiddenItems: hiddenItems?.filter(({ isExternal }) => isExternal).map(({ link }) => link),
        }),
    }),
    [openSelectFiles, openAddByLink, onApply, hiddenItems],
  );

  const options = useMemo(
    () => [
      {
        text: t("fromCatalog"),
        value: "fromCatalog",
        icon: "folder" as IconTypesKeys,
      },
      {
        text: t("byLink"),
        value: "byLink",
        icon: "link" as IconTypesKeys,
      },
    ],
    [t],
  );

  const onSelect = useCallback(
    (items: IOption<ComplexOptionText>[]) => {
      const [{ value }] = items;

      if (handlers[value]) {
        handlers[value]();

        isVisiblePopup && togglePopup();
      }
    },
    [handlers, isVisiblePopup, togglePopup],
  );

  const onMenuClick = useCallback(
    event => {
      event.stopPropagation();

      togglePopup();
    },
    [togglePopup],
  );

  return (
    <StyledMenuPopup
      isVisible={isVisiblePopup}
      onRequestClose={togglePopup}
      anchor={ref => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div ref={ref} onClick={onMenuClick}>
          {children}
        </div>
      )}
      placement={"bottom-end"}
      offset={offset}
    >
      <Menu options={options} selectOption={onSelect} />
    </StyledMenuPopup>
  );
};
